import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, AlertCircleIcon, FileTextIcon, ActivityIcon, ShieldIcon } from 'lucide-react';
export function ProductShowcase() {
  const [activeTab, setActiveTab] = useState(0);
  const tabs = ['Summary', 'Report Details', 'Benefit Breakdown'];
  return <section className="py-20 lg:py-28 bg-slate-900 overflow-hidden relative" data-id="element-285">
      {/* Background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-brand-teal/20 blur-[100px] rounded-full pointer-events-none" data-id="element-286"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-287">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-288">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-289">
            Your Clear Eligibility Report — In Under 10 Seconds*
          </motion.h2>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="text-lg text-slate-300" data-id="element-290">
            Stop hunting through raw EDI data. enSwift translates complex payer
            responses into a clean, structured interface.
          </motion.p>
        </div>

        {/* Mockup Container */}
        <motion.div initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="max-w-5xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200" data-id="element-291">
          {/* Tabs */}
          <div className="flex border-b border-slate-200 px-6 bg-white" data-id="element-292">
            {tabs.map((tab, idx) => <button key={tab} onClick={() => setActiveTab(idx)} className={`px-6 py-4 text-sm font-semibold transition-colors relative ${activeTab === idx ? 'text-brand-teal' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-293">
                {tab}
                {activeTab === idx && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-brand-teal" data-id="element-294" />}
              </button>)}
          </div>

          {/* Tab Content Area */}
          <div className="p-6 md:p-8 bg-slate-50/50 min-h-[400px]" data-id="element-295">
            <AnimatePresence mode="wait" data-id="element-296">
              {activeTab === 0 && <motion.div key="tab0" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="space-y-6" data-id="element-297">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-xl border border-slate-200 shadow-sm" data-id="element-298">
                    <div data-id="element-299">
                      <h3 className="text-2xl font-bold text-slate-900 mb-1" data-id="element-300">
                        Sarah Mitchell
                      </h3>
                      <p className="text-slate-500 font-medium" data-id="element-301">
                        DOB: 03/15/1985
                      </p>
                    </div>
                    <div className="flex items-center gap-2 bg-emerald-50 text-emerald-700 px-4 py-2 rounded-lg border border-emerald-200" data-id="element-302">
                      <CheckCircle2Icon className="w-5 h-5" data-id="element-303" />
                      <span className="font-bold tracking-wide" data-id="element-304">
                        ACTIVE COVERAGE
                      </span>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6" data-id="element-305">
                    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm" data-id="element-306">
                      <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-307">
                        <ShieldIcon className="w-4 h-4" data-id="element-308" /> Provider Details
                      </h4>
                      <div className="space-y-4" data-id="element-309">
                        <div data-id="element-310">
                          <p className="text-xs text-slate-500 mb-1" data-id="element-311">
                            Requesting Provider
                          </p>
                          <p className="font-medium text-slate-900" data-id="element-312">
                            Lakewood Family Medicine
                          </p>
                        </div>
                        <div data-id="element-313">
                          <p className="text-xs text-slate-500 mb-1" data-id="element-314">NPI</p>
                          <p className="font-medium text-slate-900" data-id="element-315">
                            1234567890
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm" data-id="element-316">
                      <h4 className="text-sm font-bold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-317">
                        <ActivityIcon className="w-4 h-4" data-id="element-318" /> Payer Details
                      </h4>
                      <div className="space-y-4" data-id="element-319">
                        <div data-id="element-320">
                          <p className="text-xs text-slate-500 mb-1" data-id="element-321">
                            Insurance Carrier
                          </p>
                          <p className="font-medium text-slate-900" data-id="element-322">
                            Blue Cross Blue Shield
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-4" data-id="element-323">
                          <div data-id="element-324">
                            <p className="text-xs text-slate-500 mb-1" data-id="element-325">
                              Payer ID
                            </p>
                            <p className="font-medium text-slate-900" data-id="element-326">
                              BCBS001
                            </p>
                          </div>
                          <div data-id="element-327">
                            <p className="text-xs text-slate-500 mb-1" data-id="element-328">
                              Subscriber ID
                            </p>
                            <p className="font-medium text-slate-900" data-id="element-329">
                              XWG-882451-01
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>}

              {activeTab === 1 && <motion.div key="tab1" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-330">
                  <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200" data-id="element-331">
                    <div className="p-6 space-y-6" data-id="element-332">
                      <div data-id="element-333">
                        <p className="text-sm font-medium text-slate-500 mb-1" data-id="element-334">
                          Plan Type
                        </p>
                        <p className="text-base font-semibold text-slate-900" data-id="element-335">
                          PPO Medical
                        </p>
                      </div>
                      <div data-id="element-336">
                        <p className="text-sm font-medium text-slate-500 mb-1" data-id="element-337">
                          Group Number
                        </p>
                        <p className="text-base font-semibold text-slate-900" data-id="element-338">
                          GRP-99281
                        </p>
                      </div>
                      <div data-id="element-339">
                        <p className="text-sm font-medium text-slate-500 mb-1" data-id="element-340">
                          Network Status
                        </p>
                        <p className="text-base font-semibold text-slate-900" data-id="element-341">
                          In-Network
                        </p>
                      </div>
                    </div>
                    <div className="p-6 space-y-6" data-id="element-342">
                      <div data-id="element-343">
                        <p className="text-sm font-medium text-slate-500 mb-1" data-id="element-344">
                          Effective Date
                        </p>
                        <p className="text-base font-semibold text-slate-900" data-id="element-345">
                          01/01/2024
                        </p>
                      </div>
                      <div data-id="element-346">
                        <p className="text-sm font-medium text-slate-500 mb-1" data-id="element-347">
                          Term Date
                        </p>
                        <p className="text-base font-semibold text-slate-900" data-id="element-348">
                          12/31/2024
                        </p>
                      </div>
                      <div data-id="element-349">
                        <p className="text-sm font-medium text-slate-500 mb-1" data-id="element-350">
                          Primary Care Physician
                        </p>
                        <p className="text-base font-semibold text-slate-900" data-id="element-351">
                          Dr. James Wilson
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>}

              {activeTab === 2 && <motion.div key="tab2" initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.2
            }} className="space-y-4" data-id="element-352">
                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-353">
                    <div className="bg-slate-50 px-6 py-4 border-b border-slate-200 flex justify-between items-center" data-id="element-354">
                      <h4 className="font-bold text-slate-900" data-id="element-355">
                        Deductible & Out-of-Pocket
                      </h4>
                      <span className="text-xs font-medium bg-brand-light text-brand-teal px-2 py-1 rounded-md" data-id="element-356">
                        In-Network
                      </span>
                    </div>
                    <div className="p-6 grid md:grid-cols-2 gap-8" data-id="element-357">
                      <div data-id="element-358">
                        <div className="flex justify-between text-sm mb-2" data-id="element-359">
                          <span className="font-medium text-slate-700" data-id="element-360">
                            Individual Deductible
                          </span>
                          <span className="font-bold text-slate-900" data-id="element-361">
                            $875 / $1,500
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2.5" data-id="element-362">
                          <div className="bg-brand-teal h-2.5 rounded-full" style={{
                        width: '58%'
                      }} data-id="element-363"></div>
                        </div>
                      </div>
                      <div data-id="element-364">
                        <div className="flex justify-between text-sm mb-2" data-id="element-365">
                          <span className="font-medium text-slate-700" data-id="element-366">
                            Out-of-Pocket Max
                          </span>
                          <span className="font-bold text-slate-900" data-id="element-367">
                            $1,200 / $6,000
                          </span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2.5" data-id="element-368">
                          <div className="bg-brand-teal h-2.5 rounded-full" style={{
                        width: '20%'
                      }} data-id="element-369"></div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-370">
                    <div className="bg-slate-50 px-6 py-4 border-b border-slate-200" data-id="element-371">
                      <h4 className="font-bold text-slate-900" data-id="element-372">
                        Service Categories
                      </h4>
                    </div>
                    <div className="divide-y divide-slate-100" data-id="element-373">
                      {[{
                    name: 'Preventive Care',
                    value: 'Covered at 100%, no deductible'
                  }, {
                    name: 'Office Visits',
                    value: '$30 copay'
                  }, {
                    name: 'Specialist Visits',
                    value: '$50 copay'
                  }, {
                    name: 'Emergency Services',
                    value: '$250 copay after deductible'
                  }].map((item, i) => <div key={i} className="px-6 py-4 flex justify-between items-center hover:bg-slate-50/50 transition-colors" data-id="element-374">
                          <span className="font-medium text-slate-700" data-id="element-375">
                            {item.name}
                          </span>
                          <span className="text-slate-900 font-semibold" data-id="element-376">
                            {item.value}
                          </span>
                        </div>)}
                    </div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>;
}